// @ts-nocheck
import { ApolloClient, from, InMemoryCache, type NormalizedCacheObject } from '@apollo/client';
import type { ApolloQueryResult, OperationVariables } from '@apollo/client/core/types';
import type { MutationOptions, QueryOptions } from '@apollo/client/core/watchQueryOptions';
import { setContext } from '@apollo/client/link/context';
import type { FetchResult } from '@apollo/client/link/core';
import { onError } from '@apollo/client/link/error';
import { getOperationName } from '@apollo/client/utilities';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { Environment } from '@cobliteam/react-environments';
import { createUploadLink } from 'apollo-upload-client';
import fetch from 'cross-fetch';
import type { DocumentNode, GraphQLError } from 'graphql';
import { getAuthHeaders } from '../account/session/AuthToken';
import { SessionManager } from '../account/session/SessionManager';
import { getOpenTelemetryBaggageHttpHeaders } from '../openTelemetryBaggage';
import HttpStatusCode from './HttpStatusCode';
import type { GraphQLRestDataSourceError } from './Types';

const env = Environment.getInstance().getEnvironment();

export class GraphQLRequester {
  private client: ApolloClient<NormalizedCacheObject>;

  private sessionManager: SessionManager;

  constructor(client: ApolloClient<NormalizedCacheObject>, sessionManager: SessionManager) {
    this.client = client;
    this.sessionManager = sessionManager;
  }

  private logRequestToDatadog = (node: DocumentNode) => {
    const operationName = getOperationName(node);
    if (operationName) {
      DatadogTrack.action(`GraphQL request in operation ${operationName}`);
    }
  };

  public query = <T, TVariables = OperationVariables>(options: QueryOptions<TVariables>): Promise<T> => {
    this.logRequestToDatadog(options.query);
    return this.sessionManager.maybeRenewAccessToken().then(() =>
      this.client
        .query({
          ...options,
          fetchPolicy: 'no-cache',
        })
        .then(this.parseGraphQLResponse)
        .catch((error) => {
          this.handleGraphQLError(error, options.query, 'query');
          throw error;
        })
    );
  };

  public mutate = <T, TVariables = OperationVariables>(options: MutationOptions<any, TVariables>): Promise<T> => {
    this.logRequestToDatadog(options.mutation);
    return this.sessionManager.maybeRenewAccessToken().then(() =>
      this.client
        .mutate(options)
        .then(this.parseGraphQLResponse)
        .catch((error) => {
          this.handleGraphQLError(error, options.mutation, 'mutation');
          throw error;
        })
    );
  };

  private parseGraphQLResponse = <T>(response: FetchResult<T> | ApolloQueryResult<T>): Promise<T> => {
    let errors;
    if (response.errors?.length) {
      errors = response.errors.map(this.parseGraphQLError);
    }
    if (!response.data) {
      if (errors) {
        return Promise.reject({ errors });
      }
      return Promise.reject(new Error('Query did not map any data'));
    }
    return Promise.resolve({ errors, ...response.data });
  };

  private parseGraphQLError = (error: GraphQLError): GraphQLRestDataSourceError => {
    const httpErrorData = error.extensions?.response || {};
    const errorCode = error.extensions?.code;
    return {
      message: error.message,
      code: errorCode,
      ...httpErrorData,
    };
  };

  private handleGraphQLError = (error: any, document: DocumentNode, operationType: 'query' | 'mutation') => {
    const operationName = getOperationName(document);
    const errorInfo = this.buildGraphQLErrorInfo(error, document, operationType);
    const errorMessage = this.buildGraphQLErrorMessage(error, operationName, operationType);

    // Create a more descriptive error object for Datadog
    const datadogError = new Error(errorMessage);
    datadogError.name = 'GraphQLRequesterError';

    DatadogTrack.error(datadogError, {
      ...errorInfo,
      EventSource: 'GraphQLRequester',
    });
  };

  private buildGraphQLErrorInfo = (error: any, document: DocumentNode, operationType: 'query' | 'mutation'): Record<string, any> => {
    const operationName = getOperationName(document);
    const errorInfo: Record<string, any> = {
      // Operation context
      operationName,
      operationType,
      graphqlHost: env.graphqlHost,

      // Error details
      errorType: this.getGraphQLErrorType(error),
      originalMessage: error.message,
      originalError: error,
    };

    // GraphQL errors
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      errorInfo.graphQLErrors = error.graphQLErrors.map((gqlError: GraphQLError) => ({
        message: gqlError.message,
        code: gqlError.extensions?.code,
        path: gqlError.path,
        locations: gqlError.locations,
        extensions: gqlError.extensions,
        httpStatus: gqlError.extensions?.response?.status,
        httpStatusText: gqlError.extensions?.response?.statusText,
      }));
    }

    // Parsed GraphQL errors from parseGraphQLResponse
    if (error.errors?.length > 0) {
      errorInfo.parsedErrors = error.errors.map((e: any) => ({
        message: e.message,
        code: e.code,
        status: e.status,
        statusText: e.statusText,
        url: e.url,
      }));
      const firstError = error.errors[0];
      if (firstError.status) {
        errorInfo.httpStatus = firstError.status;
        errorInfo.httpStatusText = firstError.statusText;
      }
    }

    // Network errors
    if (error.networkError) {
      errorInfo.networkError = {
        message: error.networkError.message,
        name: error.networkError.name,
        statusCode: error.networkError.statusCode,
        result: error.networkError.result,
      };
    }

    // HTTP response details
    if (error.networkError?.statusCode) {
      errorInfo.httpStatus = error.networkError.statusCode;
      errorInfo.httpStatusText = this.getStatusDescription(error.networkError.statusCode);
    }

    // Additional error context
    if (error.extraInfo) {
      errorInfo.extraInfo = error.extraInfo;
    }

    return errorInfo;
  };

  private buildGraphQLErrorMessage = (error: any, operationName: string | null, operationType: 'query' | 'mutation'): string => {
    const operation = operationName ? `${operationType} ${operationName}` : operationType;

    // Handle GraphQL errors
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const gqlError = error.graphQLErrors[0];
      const errorCode = gqlError.extensions?.code;
      const httpStatus = gqlError.extensions?.response?.status;

      let message = `GraphQL ${operation} failed`;

      if (httpStatus) {
        const statusDescription = this.getStatusDescription(httpStatus);
        message += ` - ${statusDescription} (${httpStatus})`;
      }

      if (errorCode) {
        message += ` - Error code: ${errorCode}`;
      }

      message += ` - ${gqlError.message}`;

      return message;
    }

    // Handle parsed GraphQL errors from parseGraphQLResponse
    if (error.errors?.length > 0) {
      const firstError = error.errors[0];

      let message = `GraphQL error in operation ${operationName} - `;

      if (firstError.status) {
        message += `${firstError.status}`;
        if (firstError.statusText) message += `: ${firstError.statusText}`;
      } else {
        message += firstError.message ?? 'Unknown error';
      }

      return message;
    }

    // Handle network errors
    if (error.networkError) {
      const networkError = error.networkError;

      if (networkError.statusCode) {
        const statusDescription = this.getStatusDescription(networkError.statusCode);
        return `GraphQL ${operation} network error - ${statusDescription} (${networkError.statusCode}): ${networkError.message}`;
      }

      return `GraphQL ${operation} network error - ${networkError.message}`;
    }

    // Handle generic errors
    return `GraphQL ${operation} failed - ${error.message ?? 'Unknown error'}`;
  };

  private getGraphQLErrorType = (error: any): string => {
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      const gqlError = error.graphQLErrors[0];
      const httpStatus = gqlError.extensions?.response?.status;

      if (httpStatus) {
        if (httpStatus >= 500) return 'GRAPHQL_SERVER_ERROR';
        if (httpStatus >= 400) return 'GRAPHQL_CLIENT_ERROR';
        return 'GRAPHQL_HTTP_ERROR';
      }

      return 'GRAPHQL_ERROR';
    }

    if (error.errors?.length > 0) {
      const httpStatus = error.errors[0].status;
      if (httpStatus) {
        if (httpStatus >= 500) return 'GRAPHQL_SERVER_ERROR';
        if (httpStatus >= 400) return 'GRAPHQL_CLIENT_ERROR';
        return 'GRAPHQL_HTTP_ERROR';
      }
      return 'GRAPHQL_ERROR';
    }

    if (error.networkError) {
      if (error.networkError.statusCode) {
        if (error.networkError.statusCode >= 500) return 'NETWORK_SERVER_ERROR';
        if (error.networkError.statusCode >= 400) return 'NETWORK_CLIENT_ERROR';
        return 'NETWORK_HTTP_ERROR';
      }
      return 'NETWORK_ERROR';
    }

    return 'UNKNOWN_ERROR';
  };

  private getStatusDescription = (status: number): string => {
    switch (status) {
      case 400:
        return 'Bad Request';
      case 401:
        return 'Unauthorized';
      case 403:
        return 'Forbidden';
      case 404:
        return 'Not Found';
      case 405:
        return 'Method Not Allowed';
      case 408:
        return 'Request Timeout';
      case 409:
        return 'Conflict';
      case 422:
        return 'Unprocessable Entity';
      case 429:
        return 'Too Many Requests';
      case 500:
        return 'Internal Server Error';
      case 502:
        return 'Bad Gateway';
      case 503:
        return 'Service Unavailable';
      case 504:
        return 'Gateway Timeout';
      default:
        return 'HTTP Error';
    }
  };
}

const uploadLink = createUploadLink({
  uri: env.graphqlHost,
  fetch,
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      ...getAuthHeaders(),
      ...getOpenTelemetryBaggageHttpHeaders(),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors?.some((error) => error.extensions?.response?.status === HttpStatusCode.NOT_FOUND)) {
    return;
  }

  const { operationName } = operation;

  if (graphQLErrors && graphQLErrors.length > 0) {
    const gqlError = graphQLErrors[0];
    const errorInfo = {
      operationName,
      errorType: 'GRAPHQL_ERROR',
      graphQLErrors: graphQLErrors.map((error) => ({
        message: error.message,
        code: error.extensions?.code,
        path: error.path,
        extensions: error.extensions,
      })),
      EventSource: 'GraphQLRequester',
    };

    const errorMessage = `GraphQL error in operation ${operationName} - ${gqlError.message}`;
    const datadogError = new Error(errorMessage);
    datadogError.name = 'GraphQLRequesterError';

    DatadogTrack.error(datadogError, {
      ...errorInfo,
      fingerprint: errorMessage,
    });
  }

  if (networkError) {
    const errorInfo = {
      operationName,
      errorType: 'NETWORK_ERROR',
      networkError: {
        message: networkError.message,
        name: networkError.name,
        statusCode: networkError.statusCode,
      },
      EventSource: 'GraphQLRequester',
    };

    const errorMessage = `Network error in operation ${operationName} - ${networkError.message}`;
    const datadogError = new Error(errorMessage);
    datadogError.name = 'GraphQLRequesterError';

    DatadogTrack.error(datadogError, errorInfo);
  }
});

const instance = new GraphQLRequester(
  new ApolloClient({
    cache: new InMemoryCache(),
    link: from([authLink, errorLink, uploadLink]),
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
  }),
  new SessionManager({ host: env.herbieApiHost })
);

export default instance;
